import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type KmRelationType, useKmEntityTypes, useKmRelationTypes } from '@/entities/ontology';
import { EntityTypeChip } from './EntityTypeChip';
import { type OntologySectionColumn, OntologySectionTable } from './OntologySectionTable';

type Props = {
  code: string;
};

/**
 * Секция «Типы связей» — только для чтения. Объединяет связи, где тип является
 * источником (from) или целью (to); дубли исключаются по коду. Строки идут сначала
 * направленными связями, затем взаимными. Без связей секция не отображается.
 */
export const EntityTypeRelationsSection: FC<Props> = ({ code }) => {
  const { t } = useTranslation('ontology');
  const { data: fromData } = useKmRelationTypes({ from: code });
  const { data: toData } = useKmRelationTypes({ to: code });
  // Справочник типов сущностей: код → человекочитаемое русское название
  const { data: entityTypesData } = useKmEntityTypes({ includeDeleted: false, limit: 1000 });

  const nameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const et of entityTypesData?.items ?? []) {
      map.set(et.code, et.name);
    }
    return map;
  }, [entityTypesData]);

  const rows = useMemo<KmRelationType[]>(() => {
    const dict = new Map<string, KmRelationType>();
    for (const rel of [...(fromData?.items ?? []), ...(toData?.items ?? [])]) {
      // Удалённые связи не показываем
      if (!rel.isDeleted && !dict.has(rel.code)) {
        dict.set(rel.code, rel);
      }
    }
    // Сначала направленные связи, затем взаимные (сортировка стабильна)
    return Array.from(dict.values()).sort((a, b) => Number(b.isDirected) - Number(a.isDirected));
  }, [fromData, toData]);

  const columns = useMemo<OntologySectionColumn<KmRelationType>[]>(() => {
    // Русское название типа сущности по коду (fallback — сам код, если не нашли в справочнике)
    const resolveName = (entityCode: string) => nameByCode.get(entityCode) ?? entityCode;
    // Второй конец связи относительно текущего типа
    const relatedCode = (rel: KmRelationType) =>
      rel.fromEntityTypeCode === code ? rel.toEntityTypeCode : rel.fromEntityTypeCode;

    return [
      {
        key: 'connection',
        header: t('relationConnection'),
        hasValue: () => true,
        // По строке на связь: чип связанной сущности; направление — стилем чипа
        cell: (rel) => (
          <EntityTypeChip code={relatedCode(rel)} name={resolveName(relatedCode(rel))} isDirected={rel.isDirected} />
        ),
      },
      {
        key: 'code',
        header: t('relationCode'),
        // «Код» всегда в одну строку без переноса и обрезки
        cellClassName: 'whitespace-nowrap',
        hasValue: (rel) => Boolean(rel.code),
        cell: (rel) => <span className="font-mono text-sm text-muted-foreground">{rel.code}</span>,
      },
      {
        key: 'description',
        header: t('relationDescription'),
        hasValue: (rel) => Boolean(rel.description),
        cell: (rel) =>
          rel.description ? <span className="text-sm text-muted-foreground">{rel.description}</span> : null,
      },
    ];
  }, [t, nameByCode, code]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <OntologySectionTable
      title={t('relationsSectionTitle')}
      columns={columns}
      rows={rows}
      getRowKey={(rel) => rel.code}
    />
  );
};
